import type { Element } from 'domhandler';

import { getElementId, getSemanticType, INLINE_CONTAINER_TAGS } from '../schema';
import type { TSequenceOp } from '../types';

// ---------------------------------------------------------------------------
// Child access
// ---------------------------------------------------------------------------

/** Returns only Element children (skips text nodes, comments, etc.). */
export const getElementChildren = (el: Element): Element[] =>
  el.children.filter((child): child is Element => child.type === 'tag');

// ---------------------------------------------------------------------------
// Identity matching
// ---------------------------------------------------------------------------

/**
 * Structural identity predicate used by the generic LCS matcher.
 *
 * Rules:
 * 1. Different semantic types never match (h1–h5 share 'heading').
 * 2. If either side carries a data-id, identity is decided by that id alone.
 * 3. Without ids, structural elements match by type (label, div, ul, etc.).
 *    Inline containers (p, h1–h5) are excluded - matching unrelated text
 *    paragraphs by type alone would produce confusing word-level diffs.
 */
export const elementsMatch = (prev: Element, curr: Element): boolean => {
  if (getSemanticType(prev.tagName) !== getSemanticType(curr.tagName)) return false;

  const prevId = getElementId(prev);
  const currId = getElementId(curr);

  if (prevId || currId) return prevId === currId;

  // Inline containers hold user text - only match by ID to avoid
  // pairing unrelated paragraphs and producing noisy word-level diffs.
  if (INLINE_CONTAINER_TAGS.has(prev.tagName)) return false;

  return true;
};

// ---------------------------------------------------------------------------
// Sequence diff (LCS)
// ---------------------------------------------------------------------------

/**
 * Exact ordered sequence diff using classic LCS with dynamic programming.
 *
 * dp[i][j] = LCS length of prevItems[i..] and currItems[j..]
 *
 * Recurrence:
 *   if equal(i, j): dp[i][j] = 1 + dp[i+1][j+1]
 *   else          : dp[i][j] = max(dp[i+1][j], dp[i][j+1])
 *
 * Tie-break: when both branches yield the same LCS length we advance the
 * current sequence first (insert).  This makes the edit script deterministic.
 *
 * There is intentionally no move operation - reorders are represented as the
 * insert/delete script induced by the chosen optimal LCS path.
 */
export const diffSequence = <T>(
  prevItems: T[],
  currItems: T[],
  isEqual: (prevItem: T, currItem: T) => boolean
): TSequenceOp<T>[] => {
  // --- Trim common prefix ---
  let prefixLen = 0;
  const minLen = Math.min(prevItems.length, currItems.length);
  while (prefixLen < minLen && isEqual(prevItems[prefixLen], currItems[prefixLen])) {
    prefixLen += 1;
  }

  // --- Trim common suffix ---
  let suffixLen = 0;
  const maxSuffix = minLen - prefixLen;
  while (
    suffixLen < maxSuffix &&
    isEqual(prevItems[prevItems.length - 1 - suffixLen], currItems[currItems.length - 1 - suffixLen])
  ) {
    suffixLen += 1;
  }

  // --- Emit prefix equal ops ---
  const operations: TSequenceOp<T>[] = [];
  for (let i = 0; i < prefixLen; i += 1) {
    operations.push({
      type: 'equal',
      prevIndex: i,
      currIndex: i,
      prevItem: prevItems[i],
      currItem: currItems[i],
    });
  }

  // --- LCS on the middle portion only ---
  const prevStart = prefixLen;
  const currStart = prefixLen;
  const prevMidLen = prevItems.length - suffixLen - prefixLen;
  const currMidLen = currItems.length - suffixLen - prefixLen;

  if (prevMidLen > 0 || currMidLen > 0) {
    // TODO(perf): If profiling shows LCS memory is a bottleneck for large middle portions,
    // consider Hirschberg's algorithm (same O(m×n) time, but O(min(m,n)) space).
    const rows = prevMidLen + 1;
    const cols = currMidLen + 1;
    const dp = Array.from({ length: rows }, () => Array.from({ length: cols }, () => 0));

    for (let pi = prevMidLen - 1; pi >= 0; pi -= 1) {
      for (let ci = currMidLen - 1; ci >= 0; ci -= 1) {
        if (isEqual(prevItems[prevStart + pi], currItems[currStart + ci])) {
          dp[pi][ci] = dp[pi + 1][ci + 1] + 1;
        } else {
          dp[pi][ci] = Math.max(dp[pi + 1][ci], dp[pi][ci + 1]);
        }
      }
    }

    let pi = 0;
    let ci = 0;

    while (pi < prevMidLen && ci < currMidLen) {
      if (isEqual(prevItems[prevStart + pi], currItems[currStart + ci])) {
        operations.push({
          type: 'equal',
          prevIndex: prevStart + pi,
          currIndex: currStart + ci,
          prevItem: prevItems[prevStart + pi],
          currItem: currItems[currStart + ci],
        });
        pi += 1;
        ci += 1;
        continue;
      }

      if (dp[pi][ci + 1] >= dp[pi + 1][ci]) {
        operations.push({
          type: 'insert',
          currIndex: currStart + ci,
          currItem: currItems[currStart + ci],
        });
        ci += 1;
        continue;
      }

      operations.push({
        type: 'delete',
        prevIndex: prevStart + pi,
        prevItem: prevItems[prevStart + pi],
      });
      pi += 1;
    }

    while (ci < currMidLen) {
      operations.push({
        type: 'insert',
        currIndex: currStart + ci,
        currItem: currItems[currStart + ci],
      });
      ci += 1;
    }

    while (pi < prevMidLen) {
      operations.push({
        type: 'delete',
        prevIndex: prevStart + pi,
        prevItem: prevItems[prevStart + pi],
      });
      pi += 1;
    }
  }

  // --- Emit suffix equal ops ---
  const prevSuffixStart = prevItems.length - suffixLen;
  const currSuffixStart = currItems.length - suffixLen;
  for (let i = 0; i < suffixLen; i += 1) {
    operations.push({
      type: 'equal',
      prevIndex: prevSuffixStart + i,
      currIndex: currSuffixStart + i,
      prevItem: prevItems[prevSuffixStart + i],
      currItem: currItems[currSuffixStart + i],
    });
  }

  return operations;
};
